import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle, MapPin, ExternalLink, Building2, ChevronRight, Info,
  Plus, Check, Pencil, Archive, RotateCcw, Star, Inbox, X, Clock,
} from 'lucide-react';
import { SITE_TYPES, SITE_TYPE_LABELS } from '@techstack/core';
import { SiteMap } from '@techstack/ui';
import apiClient from '../../lib/apiClient';
import { accountKey } from './accountsFormat';
import {
  SiteTechSection, TechCoverageStrip, TechReviewQueue,
} from './AccountsLocationsTech';
import AddressField from './AccountsLocationsAddress';
import SitePhotos from './AccountsLocationsPhotos';
import SiteEmbedMap from './AccountsLocationsMap';

/*
 * AccountsLocationsTab — where this account physically is.
 *
 * S3 of docs/AGENCY-SITE-LOCATIONS-ROADMAP.md, and the first user-visible
 * slice of the whole subsystem.
 *
 * Three things here are policy rather than styling:
 *
 *  1. COVERAGE IS ALWAYS ON SCREEN. The seed is a floor, never a ceiling
 *     (§3.4), so the header says where these rows came from and whether DGS's
 *     inventory is known to under-report this agency. "42 sites" must never
 *     read as "all 42 sites" — that is the difference between a tool a rep
 *     trusts and one that embarrasses them in front of a customer.
 *
 *  2. STAFFED TYPES RENDER BY DEFAULT, everything else is one toggle away
 *     (decision §12.2). Caltrans has 3,656 SPI structures; without this the
 *     first page is barns and restrooms and nobody opens the tab twice.
 *
 *  3. ESTIMATES LOOK LIKE ESTIMATES. A headcount derived from square footage
 *     renders as a span with a "~" and an explanation on hover, never as a
 *     number. Same for a site type inferred from a building mix.
 *
 * Reads /api/sites (JWT), NOT a /api/v1 route: boundary decision #26 keeps
 * site reads off the key-only surface.
 *
 * S4 adds the authoring half, and it ships BEFORE the web hunt on purpose: a
 * pipeline that fills faster than a human can fix produces a confidently wrong
 * dataset. Everything typed here lands with `verified` provenance, which is
 * what stops the weekly DGS refresh from overwriting it — so the "Confirm"
 * button is not cosmetic, it is how a row becomes permanent.
 *
 * S7 adds technology at a location (`AccountsLocationsTech.js`), which is the
 * same pattern one layer in: the estate per site, a coverage line that states
 * the gap rather than implying completeness, and a review queue for what an
 * assistant proposed. Its vocabularies come straight from @techstack/core —
 * the backend already keeps a checked mirror, and a third hand-typed copy here
 * would be the one nobody checks.
 */

// Both come from the contract. The hand-typed copies that used to live here
// were identical to it on the day they were written, which is the only day a
// divergent copy is ever right.
const EDITABLE_TYPES = SITE_TYPES;
const TYPE_LABELS = SITE_TYPE_LABELS;

/*
 * How many addresses the city drill lists.
 *
 * Fixed, and deliberately small. The alternative — showing as many as fit —
 * needs the widget's height, which means measuring, which is what put a render
 * loop in the map in the first place. And a scroller inside the panel is not
 * an option: "scrolling in the widget is never the answer" (Ryan). So the list
 * is bounded by a number that fits every shape the board allows, the header
 * states the true total beside the city name, and the rest is one click away.
 */
const DRILL_ROWS = 4;

const SOURCE_LABELS = {
  spi: 'DGS inventory',
  catalog: 'occupancy catalog',
  locator: 'agency locator',
  search: 'web search',
  assistant: 'assistant',
  manual: 'entered by hand',
  notes: 'account notes',
};

function fmtAddress(s) {
  const line = [s.addressLine1, s.addressLine2].filter(Boolean).join(', ');
  const tail = [s.city, s.state, s.postalCode].filter(Boolean).join(' ');
  return [line, tail].filter(Boolean).join(', ');
}

/** A headcount that reads as what it is. */
function headcount(s) {
  if (s.employeeCount != null) return { text: `${s.employeeCount.toLocaleString()} staff`, estimated: false, title: null };
  if (s.employeeEstimate && s.employeeEstimate.low != null) {
    const e = s.employeeEstimate;
    return {
      text: `~${e.low}–${e.high}`,
      estimated: true,
      title: `Estimated from ${Number(e.sqFt).toLocaleString()} sq ft of office space. `
        + 'Not an observed number — overwrite it and yours wins permanently.',
    };
  }
  if (s.employeeBand) return { text: s.employeeBand, estimated: true, title: 'Banded estimate' };
  return null;
}

function ProvenanceChip({ site }) {
  const p = site.provenance || {};
  if (p.status === 'field') {
    return <span className="accounts-chip" title="Confirmed by someone at the account">confirmed in the field</span>;
  }
  if (p.status === 'verified') {
    return <span className="accounts-chip" title="You confirmed this">verified</span>;
  }
  const src = SOURCE_LABELS[site.source] || site.source;
  const title = p.guessedFields
    ? `From the ${src}. ${p.guessedFields} field${p.guessedFields === 1 ? '' : 's'} on this row are estimates, not observations.`
    : `From the ${src}. Nobody has confirmed it yet.`;
  return <span className="accounts-chip sites-chip-auto" title={title}>{src}</span>;
}

function CoverageLine({ coverage, acronym }) {
  if (!coverage) return null;
  const parts = Object.entries(coverage.bySource || {})
    .sort((a, b) => b[1] - a[1])
    .map(([src, n]) => `${n} from the ${SOURCE_LABELS[src] || src}`);

  return (
    <div className="sites-coverage">
      <div className="sites-coverage-main">
        <strong>{coverage.total}</strong> {coverage.total === 1 ? 'site' : 'sites'} on record
        {parts.length > 0 && <> — {parts.join(', ')}</>}
        {coverage.withAddress < coverage.total && (
          <> · {coverage.total - coverage.withAddress} without a street address</>
        )}
      </div>
      {coverage.seedUnderreports && (
        <div className="sites-coverage-warn">
          <Info size={13} aria-hidden />
          <span>
            This is a floor, not a complete list. DGS's inventory files property under
            whoever <em>holds</em> it, so buildings {acronym || 'this agency'} occupies in
            state-owned space only appear once someone identifies them.
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * How current this account's footprint is, and what fell out of the inventory.
 *
 * The drain slice. A weekly sweep can detect something nothing else can — DGS
 * stopped listing a property we hold — and an address that has quietly stopped
 * being real is the most embarrassing thing this tab can show a customer.
 *
 * Two things it is careful about, both of which would otherwise make good data
 * look suspect:
 *
 *  - A hand-entered site has no seed to disappear from, so it is counted as
 *    "not refreshed by anything" rather than as missing. That distinction is
 *    computed server-side per source; this only renders it.
 *  - Nothing here is a verdict. A site can fall out of SPI because the office
 *    closed, because DGS re-filed it under another agency, or because DGS had
 *    a bad export day. The buttons are the ordinary ones — open it, confirm
 *    it, close it — and closing keeps the row.
 */
function FreshnessLine({ account }) {
  const { data } = useQuery({
    queryKey: ['accounts-sites-freshness', account],
    queryFn: () => apiClient.get('/api/sites/freshness', { params: { dept: account } }),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });
  const { data: dropped } = useQuery({
    queryKey: ['accounts-sites-dropped', account],
    queryFn: () => apiClient.get('/api/sites/dropped', { params: { dept: account } }),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });

  const rows = dropped?.dropped || [];
  if (!data || !data.total) return null;

  return (
    <div className="sites-freshness">
      <div className={data.overdue ? 'sites-coverage-warn' : 'sites-muted'}>
        {data.overdue ? <AlertCircle size={13} aria-hidden /> : <Clock size={13} aria-hidden />}
        <span>{data.basis}</span>
      </div>

      {rows.length > 0 && (
        <details className="sites-dropped">
          <summary>
            {rows.length} {rows.length === 1 ? 'site is' : 'sites are'} no longer in the DGS
            inventory
          </summary>
          <p className="sites-muted">
            The last complete refresh did not list {rows.length === 1 ? 'it' : 'them'}. That
            usually means the property moved, was re-filed under another agency, or the office
            closed — it is a prompt to check, not a closure. Nothing has been changed.
          </p>
          <ul className="sites-tech-list">
            {rows.map((r) => (
              <li key={r.siteId} className="sites-tech-row">
                <div className="sites-tech-main">
                  <span className="sites-tech-label">{r.siteName}</span>
                  <div className="sites-sub">
                    {[r.city, SOURCE_LABELS[r.source] || r.source].filter(Boolean).join(' · ')}
                    {r.daysSinceSeen != null && ` · last listed ${r.daysSinceSeen} days ago`}
                  </div>
                  {r.humanConfirmed && (
                    <div className="sites-sub">
                      You confirmed this one — trust the row over the sweep.
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <p className="sites-muted">
            Close one from its row below if it really has gone. Closing keeps it on record —
            &ldquo;they used to be at 2nd and Capitol&rdquo; is intelligence.
          </p>
        </details>
      )}
    </div>
  );
}

/**
 * The add / edit form.
 *
 * Deliberately small: name, type, address, headcount. Everything else the
 * backend either derives or leaves alone. A form that asks for thirteen fields
 * is a form nobody fills in, and an empty row helps nobody.
 */
function SiteForm({ site, account, onDone, onCancel }) {
  const queryClient = useQueryClient();
  const editing = !!site;
  const [f, setF] = useState({
    siteName: site?.siteName || '',
    siteType: site?.siteType || 'office',
    addressLine1: site?.addressLine1 || '',
    addressLine2: site?.addressLine2 || '',
    city: site?.city || '',
    state: site?.state || 'CA',
    postalCode: site?.postalCode || '',
    employeeCount: site?.employeeCount ?? '',
    notes: site?.notes || '',
    // Filled by the address field when Google names a listing. The only
    // Places value that may be stored (their policy exempts the place ID and
    // nothing else), and what makes the map link land on the building rather
    // than on a text search for the street.
    googlePlaceId: site?.googlePlaceId || '',
  });
  const [err, setErr] = useState(null);

  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  /*
   * The address field proposes; these boxes still hold the truth.
   *
   * A resolved address fills the fields and the reader can then correct any
   * of them before saving — which is what makes the row something a person
   * adopted rather than something an API asserted. A name the reader already
   * typed is never overwritten: they meant it.
   */
  const applyResolved = React.useCallback((fields) => {
    setF((prev) => ({
      ...prev,
      ...fields,
      siteName: prev.siteName.trim() ? prev.siteName : fields.siteName,
    }));
  }, []);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...f,
        employeeCount: f.employeeCount === '' ? null : Number(f.employeeCount),
      };
      return editing
        ? apiClient.patch(`/api/sites/${site.siteId}`, body)
        : apiClient.post('/api/sites', { ...body, dept: account });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts-sites', account] });
      onDone();
    },
    onError: (e) => {
      // A duplicate names the site that already exists, so say which one
      // rather than just refusing.
      //
      // `e.data`, not `e.response.data`: apiClient is a fetch wrapper, not
      // axios, and it hangs the parsed error body straight off the Error
      // (see parseResponse). Reading the axios shape here meant EVERY backend
      // refusal — a missing address, a duplicate, a read-only plan — arrived
      // as the generic fallback, so the one message that would have explained
      // the failure was the one message we threw away.
      const d = e?.data;
      setErr(d?.existing
        ? `A site at this address already exists: ${d.existing.siteName}`
        : (d?.error || e.message || 'Could not save'));
    },
  });

  return (
    <form
      className="sites-form"
      onSubmit={(e) => { e.preventDefault(); setErr(null); save.mutate(); }}
    >
      <h4>{editing ? 'Edit site' : 'Add a site'}</h4>
      {!editing && (
        <p className="sites-muted">
          For places DGS&apos;s inventory does not hold — a data center, a leased suite,
          anything you know about and it does not.
        </p>
      )}

      <div className="sites-form-grid">
        <label>
          Name
          <input value={f.siteName} onChange={set('siteName')} maxLength={200} required />
        </label>
        <label>
          Type
          <select value={f.siteType} onChange={set('siteType')}>
            {EDITABLE_TYPES.map((t) => (
              <option key={t} value={t}>{TYPE_LABELS[t]}</option>
            ))}
          </select>
        </label>
        <div className="sites-form-wide">
          <AddressField onResolved={applyResolved} disabled={save.isPending} />
        </div>
        <label className="sites-form-wide">
          Street address
          <input value={f.addressLine1} onChange={set('addressLine1')} />
        </label>
        <label>
          Suite / floor
          <input value={f.addressLine2} onChange={set('addressLine2')} />
        </label>
        <label>
          City
          <input value={f.city} onChange={set('city')} />
        </label>
        <label>
          ZIP
          <input value={f.postalCode} onChange={set('postalCode')} maxLength={10} />
        </label>
        <label>
          People (leave blank if unknown)
          <input
            type="number" min="0" value={f.employeeCount} onChange={set('employeeCount')}
            placeholder="unknown"
          />
        </label>
        <label className="sites-form-wide">
          Notes
          <input value={f.notes} onChange={set('notes')} maxLength={4000} />
        </label>
      </div>

      {err && <div className="accounts-error"><AlertCircle size={14} /> {err}</div>}

      <div className="sites-form-actions">
        <button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : (editing ? 'Save' : 'Add site')}
        </button>
        <button type="button" className="sites-btn-quiet" onClick={onCancel}>Cancel</button>
        {!editing && (
          <span className="sites-muted">
            Anything you enter is pinned — the DGS refresh will not overwrite it.
          </span>
        )}
      </div>
    </form>
  );
}

/** The verbs that act on one row. */
function RowActions({ site, account, onEdit }) {
  const queryClient = useQueryClient();
  const [err, setErr] = useState(null);
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['accounts-sites', account] });
    queryClient.invalidateQueries({ queryKey: ['site-detail', site.siteId] });
  };
  const act = useMutation({
    mutationFn: (what) => {
      if (what === 'confirm') return apiClient.post(`/api/sites/${site.siteId}/verify`, {});
      if (what === 'close') return apiClient.post(`/api/sites/${site.siteId}/close`, {});
      if (what === 'reopen') return apiClient.post(`/api/sites/${site.siteId}/reopen`, {});
      return apiClient.post(`/api/sites/${site.siteId}/primary`, {});
    },
    onSuccess: () => { setErr(null); refresh(); },
    // These four had NO error handler at all, so a refusal — an already-closed
    // site, a read-only plan, an expired session — left the button un-pressed
    // and said nothing. A verb that can fail silently is a verb the reader
    // stops trusting.
    onError: (e) => setErr(e?.data?.error || e.message || 'That did not go through'),
  });

  const confirmed = site.provenance?.status === 'verified' || site.provenance?.status === 'field';

  return (
    <div className="sites-actions">
      <button type="button" onClick={() => onEdit(site)} title="Correct this site">
        <Pencil size={13} aria-hidden /> Edit
      </button>
      {!confirmed && (
        <button
          type="button" disabled={act.isPending} onClick={() => act.mutate('confirm')}
          title="Pin every field on this row so the DGS refresh cannot overwrite it"
        >
          <Check size={13} aria-hidden /> Confirm
        </button>
      )}
      {site.status === 'active' && !site.isPrimary && (
        <button type="button" disabled={act.isPending} onClick={() => act.mutate('primary')} title="Mark as headquarters">
          <Star size={13} aria-hidden /> HQ
        </button>
      )}
      {site.status === 'active' ? (
        <button type="button" disabled={act.isPending} onClick={() => act.mutate('close')} title="They moved out. The row is kept, not deleted.">
          <Archive size={13} aria-hidden /> Closed
        </button>
      ) : (
        <button type="button" disabled={act.isPending} onClick={() => act.mutate('reopen')}>
          <RotateCcw size={13} aria-hidden /> Reopen
        </button>
      )}
      {err && (
        <div className="accounts-error sites-row-error">
          <AlertCircle size={13} aria-hidden /> {err}
        </div>
      )}
    </div>
  );
}

function SiteRow({ site, onOpen, account, onEdit }) {
  const hc = headcount(site);
  const address = fmtAddress(site);
  return (
    <tr className={site.status === 'closed' ? 'sites-row-closed' : undefined}>
      <td>
        <button type="button" className="sites-name-btn" onClick={() => onOpen(site)}>
          {site.siteName}
          {site.isPrimary && <span className="accounts-chip sites-chip-hq">HQ</span>}
          {site.status === 'closed' && <span className="accounts-chip">closed</span>}
        </button>
        {site.spiStructureCount > 1 && (
          <div className="sites-sub">{site.spiStructureCount} buildings</div>
        )}
      </td>
      <td>{TYPE_LABELS[site.siteType] || site.siteType}</td>
      <td>
        {address || <span className="sites-muted">no address on record</span>}
      </td>
      <td>
        {hc
          ? <span className={hc.estimated ? 'sites-estimate' : undefined} title={hc.title || undefined}>{hc.text}</span>
          : <span className="sites-muted">—</span>}
      </td>
      <td><ProvenanceChip site={site} /></td>
      <td>
        {site.mapsUrl ? (
          // "Pinned" means the link carries a Google place ID that sits within
          // 200m of DGS's own coordinate for this address, so the map opens on
          // the building rather than on a text search for the street. "Map" is
          // the search: right most of the time, and honest about being a search.
          <a
            className="sites-map-link"
            href={site.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={site.googlePlaceId
              ? 'Pinned to the Google listing that matches DGS\u2019s coordinate for this address'
              : 'Search Google Maps for this address'}
          >
            <MapPin size={13} aria-hidden /> {site.googlePlaceId ? 'Pinned' : 'Map'} <ExternalLink size={11} aria-hidden />
          </a>
        ) : <span className="sites-muted">—</span>}
      </td>
      <td><RowActions site={site} account={account} onEdit={onEdit} /></td>
    </tr>
  );
}

function SiteDetail({ site, onClose, onOpenTab }) {
  const { data, isLoading } = useQuery({
    queryKey: ['site-detail', site.siteId],
    queryFn: () => apiClient.get(`/api/sites/${site.siteId}`),
    staleTime: 5 * 60 * 1000,
  });

  const structures = data?.structures || [];
  const tech = data?.tech || [];

  return (
    <div className="sites-detail">
      <div className="sites-detail-head">
        <h4>{site.siteName}</h4>
        <button type="button" className="sites-detail-close" onClick={onClose}>Close</button>
      </div>
      <p className="sites-detail-address">{fmtAddress(site) || 'No address on record'}</p>

      {isLoading && <p className="accounts-panel-note">Loading…</p>}

      {!isLoading && (
        <>
          <SiteEmbedMap site={site} />

          <SitePhotos site={site} />

          <SiteTechSection
            site={site}
            tech={tech}
            summary={data?.techSummary}
            onOpenTab={onOpenTab}
          />

          {structures.length > 0 && (
            <section>
              <h5>Buildings ({structures.length})</h5>
              <table className="sites-structures">
                <tbody>
                  {structures.slice(0, 40).map((b) => (
                    <tr key={b.structureId}>
                      <td>{b.structureName || '—'}</td>
                      <td className="sites-muted">{b.structureType || ''}</td>
                      <td>{b.sqFt ? `${b.sqFt.toLocaleString()} sq ft` : ''}</td>
                      <td className="sites-muted">{b.yearBuilt || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {structures.length > 40 && (
                <p className="sites-muted">…and {structures.length - 40} more</p>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}

/**
 * What a machine proposed, waiting on a person.
 *
 * Mirrors the branding library's review queue. Candidates are excluded from
 * the main list by the server (`VISIBLE_STATUSES`), so nothing here is being
 * shown as fact — this is the one place they appear, and the only way out is
 * a human clicking approve or reject.
 */
function ReviewQueue({ account }) {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['accounts-site-candidates', account],
    queryFn: () => apiClient.get('/api/sites/candidates', { params: { dept: account } }),
    enabled: !!account,
    staleTime: 60 * 1000,
  });

  const act = useMutation({
    mutationFn: ({ siteId, verb }) => apiClient.post(`/api/sites/${siteId}/${verb}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts-site-candidates', account] });
      queryClient.invalidateQueries({ queryKey: ['accounts-sites', account] });
    },
  });

  const rows = data?.candidates || [];
  if (!rows.length) return null;

  return (
    <section className="sites-review">
      <h4>
        <Inbox size={14} aria-hidden /> Waiting for review ({rows.length})
      </h4>
      <p className="sites-muted">
        Found in a published agency locator. None of these appear in the list below
        until you approve them.
      </p>
      <table className="sites-table">
        <tbody>
          {rows.map((c) => (
            <tr key={c.siteId}>
              <td>
                <strong>{c.siteName}</strong>
                <div className="sites-sub">
                  {[c.addressLine1, c.city, c.state, c.postalCode].filter(Boolean).join(', ')
                    || 'no address'}
                </div>
                {c.basis && <div className="sites-sub">{c.basis}</div>}
              </td>
              <td>{TYPE_LABELS[c.siteType] || c.siteType}</td>
              <td>
                <div className="sites-actions">
                  <button
                    type="button" disabled={act.isPending}
                    onClick={() => act.mutate({ siteId: c.siteId, verb: 'approve' })}
                  >
                    <Check size={13} aria-hidden /> Approve
                  </button>
                  <button
                    type="button" disabled={act.isPending}
                    onClick={() => act.mutate({ siteId: c.siteId, verb: 'reject' })}
                    title="Kept as rejected, so the next run does not propose it again"
                  >
                    <X size={13} aria-hidden /> Reject
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/**
 * @param {boolean} compact  the board's contract with a panel: the widget shell
 *   owns the title and the way into the full view, so the cell shows the SITES
 *   and nothing else — no add form, no review queues, no coverage strip. The
 *   queues in particular must not live in two places: a rep approving a
 *   candidate in a 6-column cell, next to a second copy of the same queue on
 *   the tab, is two answers to one question.
 */
export default function AccountsLocationsTab({ selectedDepartment, onOpenTab, compact = false }) {
  const account = accountKey(selectedDepartment);
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(null);
  // The city the reader clicked on the map, and its sites. Compact only.
  const [city, setCity] = useState(null);
  // null = closed, 'new' = add form, a site object = edit that row.
  const [editing, setEditing] = useState(null);

  const { data, isLoading, error } = useQuery({
    // `showAll` belongs in the key — it changes what the server returns, so a
    // flip must refetch rather than re-render stale rows.
    queryKey: ['accounts-sites', account, showAll],
    queryFn: () => apiClient.get('/api/sites', {
      params: { dept: account, ...(showAll ? { all: 'true' } : {}) },
    }),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });

  const sites = useMemo(() => data?.sites || [], [data]);
  const coverage = data?.coverage;

  if (!account) {
    return <p className="accounts-panel-note">Select an agency to see its locations.</p>;
  }
  if (isLoading) {
    return <div className="accounts-panel-note">Loading locations…</div>;
  }
  if (error) {
    // The migration is applied by hand, so "not set up yet" is a real state
    // and deserves a better answer than a red error box.
    const notMigrated = error?.data?.code === 'SITES_NOT_MIGRATED'
      || /not set up/i.test(error?.message || '');
    if (notMigrated) {
      return (
        <p className="accounts-panel-note">
          Site locations are not set up on this database yet. Apply migration
          <code> 1812800000000_agency_site_locations</code> and run
          <code> scripts/sites-ingest.js --all</code>.
        </p>
      );
    }
    return (
      <div className="accounts-error">
        <AlertCircle size={16} /> {error.message || 'Failed to load locations'}
      </div>
    );
  }

  const hiddenCount = coverage ? Math.max(0, coverage.total - coverage.staffed) : 0;

  return (
    <div className={`sites-tab${compact ? ' sites-tab--compact' : ''}${compact && city ? ' sites-tab--drilled' : ''}`}>
      {/* Compact reads its own, better line off the map: the strip said "106
          sites on record" above a list of 101 and never said where the other
          five went. */}
      {!compact && <CoverageLine coverage={coverage} acronym={data?.acronym} />}

      {!compact && <FreshnessLine account={account} />}

      {!compact && (
        <>
          <ReviewQueue account={account} />
          <TechReviewQueue account={account} />
          <TechCoverageStrip account={account} />

          <div className="sites-toolbar">
            {!editing && (
              <button type="button" className="sites-add-btn" onClick={() => setEditing('new')}>
                <Plus size={14} aria-hidden /> Add a site
              </button>
            )}
          </div>

          {editing && (
            <SiteForm
              site={editing === 'new' ? null : editing}
              account={account}
              onDone={() => setEditing(null)}
              onCancel={() => setEditing(null)}
            />
          )}
        </>
      )}

      {!compact && hiddenCount > 0 && (
        <label className="sites-toggle">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Show all facilities
          <span className="sites-muted">
            {' '}(+{hiddenCount} yard{hiddenCount === 1 ? '' : 's'}, warehouses and land)
          </span>
        </label>
      )}

      {sites.length === 0 ? (
        <p className="accounts-panel-note">
          No locations on record for this account yet.
          {coverage?.total > 0 && !showAll && ' Try “Show all facilities”.'}
          {!editing && ' Or add the first one by hand.'}
        </p>
      ) : compact ? (
        /*
         * On the board the estate is a MAP, not a table.
         *
         * Measured against EDD's 101 rows, four of the table's six columns
         * carried almost nothing — `source` had one distinct value, `type` was
         * "office" 83 times, the map column read "Pinned" 91 times, and
         * `people` was blank on 81. The two that worked were the name and the
         * address, and the address's real payload is which city, across 65 of
         * them. The tile also showed 8 of 101 rows and spent ~5 controls on
         * each. A map answers the column that mattered, at a glance, and
         * editing moves to the full view where it belongs.
         */
        <>
          <SiteMap
            sites={sites}
            coverage={coverage}
            fill
            onPick={setCity}
            onOpenAll={onOpenTab ? () => onOpenTab('locations') : undefined}
          />
          {city && (
            <div className="sites-city">
              <div className="sites-city-head">
                <strong>{city.city}</strong>
                <span className="sites-muted">
                  {city.count} {city.count === 1 ? 'site' : 'sites'}
                </span>
                <button type="button" className="sites-city-close" onClick={() => setCity(null)}>
                  Clear
                </button>
              </div>
              <ul className="sites-city-list">
                {city.sites.slice(0, DRILL_ROWS).map((s) => (
                  <li key={s.siteId}>
                    <button type="button" className="sites-city-name" onClick={() => setOpen(s)}>
                      {s.siteName}
                    </button>
                    {s.addressLine1 && <span className="sites-muted"> · {s.addressLine1}</span>}
                  </li>
                ))}
              </ul>
              {city.count > DRILL_ROWS && (
                <p className="sites-muted">
                  …and {city.count - DRILL_ROWS} more — open the full view
                </p>
              )}
            </div>
          )}
        </>
      ) : (
        <table className="sites-table">
          <thead>
            <tr>
              <th>Site</th><th>Type</th><th>Address</th><th>People</th><th>Source</th><th>Map</th><th />
            </tr>
          </thead>
          <tbody>
            {(compact ? sites.slice(0, 8) : sites).map((s) => (
              <SiteRow
                key={s.siteId} site={s} onOpen={setOpen} account={account}
                onEdit={setEditing}
              />
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <SiteDetail site={open} onClose={() => setOpen(null)} onOpenTab={onOpenTab} />
      )}

      {coverage?.lastIngestAt && (
        <p className="sites-footnote">
          <Building2 size={12} aria-hidden /> DGS inventory last pulled{' '}
          {new Date(coverage.lastIngestAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          <ChevronRight size={12} aria-hidden />
        </p>
      )}
    </div>
  );
}
